import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setError('Invalid reset link. Please request a new password reset.');
      setValidatingToken(false);
      return;
    }

    setToken(tokenFromUrl);
    validateToken(tokenFromUrl);
  }, [searchParams]);

  const validateToken = async (tokenToValidate) => {
    try {
      const response = await axios.get(
        `http://localhost:9090/api/auth/validate-reset-token?token=${tokenToValidate}`
      );
      
      if (response.data.valid) {
        setTokenValid(true);
      } else {
        setError('This reset link is invalid or has expired. Please request a new one.');
      }
    } catch (err) {
      setError('Failed to validate reset link. Please try again.');
    } finally {
      setValidatingToken(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post('http://localhost:9090/api/auth/reset-password', {
        token,
        newPassword
      });
      
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Reset Password</h1>
            <p>Validating your reset link...</p>
          </div>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Reset Password</h1>
          </div>

          <div className="alert alert-error">
            {error || 'This reset link is invalid or has expired.'}
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <button 
              onClick={() => navigate('/forgot-password')} 
              className="btn-primary"
            >
              Request New Reset Link
            </button>
          </div>

          <div className="auth-footer">
            <p>
              Remember your password?{' '}
              <span onClick={() => navigate('/login')} className="link">
                Back to Login
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Reset Password</h1>
          <p>Enter your new password below</p>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              disabled={loading}
              minLength="6"
            />
            <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
              Must be at least 6 characters
            </small>
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              disabled={loading}
              minLength="6"
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>

          <div className="auth-footer">
            <p>
              Remember your password?{' '}
              <span onClick={() => navigate('/login')} className="link">
                Back to Login
              </span>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
